import { useState } from 'react';
import { PageViewerControls, StatusChip } from '../../components/index.js';

/**
 * Presentational modules for the RAPID v2 Metrics & Value redesign (PRD MV-001…MV-073).
 * Mirrors the as-built Diagnosis view-switcher pattern (DiagnosisSections.jsx): all copy is
 * seeded/derived; these components are pure render functions. The owning MetricsTab keeps the
 * filter + active-view state and renders every view mounted (toggled with the `hidden` attribute)
 * so deterministic counts, metric IDs, and existing selectors keep resolving.
 */

export const METRICS_SECTIONS = [
  { key: 'summary', label: 'Summary' },
  { key: 'brief-safe', label: 'Brief-Safe Now' },
  { key: 'cost-proof', label: 'Cost Proof' },
  { key: 'bowler', label: 'Bowler Trend' },
  { key: 'library', label: 'Metrics Library' },
  { key: 'trust-gaps', label: 'Trust Gaps' },
  { key: 'sustainment', label: 'Sustainment' },
];

// ---------------------------------------------------------------------------
// Derivation helpers
// ---------------------------------------------------------------------------

export function deriveMetricTrust(metrics = []) {
  const confirmed = metrics.filter((m) => m.sourceStatus === 'Confirmed');
  const needsConfirmation = metrics.filter((m) => m.sourceStatus === 'Needs confirmation');
  const dataDefinition = metrics.filter((m) => m.measureType === 'Data Definition');
  return { confirmed, needsConfirmation, dataDefinition };
}

function trustCount(trust, key) {
  return Number(trust?.counts?.[key] ?? trust?.[key]?.length ?? 0);
}

function parsePct(value) {
  const parsed = Number(String(value ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
}

function slug(value = '') {
  return String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Least-squares linear fit over a [value, value, ...] series (index used as x).
// Powers the official DHA bowler "Linear (...)" trendline. Pure helper, no charting lib.
function linearFit(values = []) {
  const pts = values
    .map((v, i) => [i, v])
    .filter(([, v]) => typeof v === 'number' && Number.isFinite(v));
  const n = pts.length;
  if (n < 2) return null;
  const sx = pts.reduce((s, [px]) => s + px, 0);
  const sy = pts.reduce((s, [, py]) => s + py, 0);
  const sxx = pts.reduce((s, [px]) => s + px * px, 0);
  const sxy = pts.reduce((s, [px, py]) => s + px * py, 0);
  const denom = n * sxx - sx * sx;
  if (!denom) return null;
  const slope = (n * sxy - sx * sy) / denom;
  const intercept = (sy - slope * sx) / n;
  return { slope, intercept };
}

// ---------------------------------------------------------------------------
// Section nav (view-switcher — mirrors DiagnosisSectionNav)
// ---------------------------------------------------------------------------

export function MetricsSectionNav({ active, onSelect }) {
  const expanded = active === 'all';
  return (
    <nav className="metrics-section-nav" data-testid="metrics-section-nav">
      <div className="metrics-nav-tabs" role="tablist" aria-label="Metrics and value sections">
        {METRICS_SECTIONS.map((section) => (
          <button
            key={section.key}
            type="button"
            role="tab"
            aria-selected={active === section.key}
            className={`metrics-nav-chip${active === section.key ? ' is-active' : ''}`}
            data-testid={`metrics-nav-${section.key}`}
            onClick={() => onSelect(section.key)}
          >
            {section.label}
          </button>
        ))}
      </div>
      <button
        type="button"
        className={`metrics-nav-chip metrics-nav-expand${expanded ? ' is-active' : ''}`}
        data-testid="metrics-expand-all"
        aria-pressed={expanded}
        onClick={() => onSelect(expanded ? 'summary' : 'all')}
      >
        {expanded ? 'Collapse' : 'Expand all'}
      </button>
    </nav>
  );
}

// ---------------------------------------------------------------------------
// 7.1 Trust / Value Summary
// ---------------------------------------------------------------------------

export function MetricsValueSummaryPanel({
  valueSummary,
  costAvoidance,
  trust,
  cadence,
  confidence,
}) {
  const exact = costAvoidance?.totalCostAvoidance;
  const exactLabel = exact != null ? `$${Number(exact).toLocaleString()}` : '—';
  return (
    <section
      className="rapid-panel metrics-value-summary-panel"
      data-testid="metrics-value-summary-panel"
    >
      <div className="metrics-summary-head">
        <div className="eyebrow">Trust &amp; value summary</div>
        <h3 className="metrics-summary-headline">
          Current value case: directionally strong, source validation pending.
        </h3>
      </div>

      <div className="metrics-summary-stats">
        <div className="metrics-summary-stat is-primary">
          <span className="k">Estimated value</span>
          <span className="v" data-testid="metrics-summary-value-display">
            {valueSummary?.displayValue || '—'}
          </span>
          <span className="sub" data-testid="metrics-summary-exact-value">
            {exactLabel} exact model
          </span>
        </div>
        <div className="metrics-summary-stat">
          <span className="k">Brief-safe metrics</span>
          <span className="v" data-testid="metrics-summary-confirmed-count">
            {trustCount(trust, 'confirmed')}
          </span>
          <span className="sub">confirmed source</span>
        </div>
        <div className="metrics-summary-stat">
          <span className="k">Needs confirmation</span>
          <span className="v" data-testid="metrics-summary-needs-confirmation-count">
            {trustCount(trust, 'needsConfirmation')}
          </span>
          <span className="sub" data-testid="metrics-summary-data-definition-count">
            incl. {trustCount(trust, 'dataDefinition')} data-definition
          </span>
        </div>
        <div className="metrics-summary-stat">
          <span className="k">Confidence</span>
          <span className="v is-text" data-testid="metrics-summary-confidence">
            {confidence || 'Estimated / medium-low'}
          </span>
          <span className="sub">Assumption-based estimate</span>
        </div>
      </div>

      <div className="metrics-summary-notes">
        <div className="metrics-summary-note is-safe">
          <h4>Brief-safe now</h4>
          <p>
            Leadership can brief the current value model, the {trustCount(trust, 'confirmed')}{' '}
            confirmed metrics, and the current improvement direction as briefing evidence.
          </p>
        </div>
        <div className="metrics-summary-note is-caveat">
          <h4>Needs validation</h4>
          <p>
            Denominator ownership, source-system confirmation, and slot-time assumptions must be
            confirmed before making a production value claim.
          </p>
        </div>
      </div>

      <div className="metrics-summary-next" data-testid="metrics-summary-next-validation">
        <span className="chip outline sm">Next validation</span>
        <p>
          Confirm the eligible-population denominator and named source-system owners, then re-grade
          the {trustCount(trust, 'needsConfirmation')} needs-confirmation metrics. Sustainment
          cadence: {cadence || '30 / 60 / 90 days, then quarterly'}.
        </p>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 7.3 Brief-Safe Now
// ---------------------------------------------------------------------------

function MetricMiniCard({ metric, testId }) {
  return (
    <li className="metrics-mini-card" data-testid={testId}>
      <div className="row spread">
        <strong>{metric.id}</strong>
        <StatusChip status={String(metric.status).toLowerCase()}>{metric.status}</StatusChip>
      </div>
      <div className="metrics-mini-name">{metric.name}</div>
      <div className="metrics-mini-values muted small">
        {metric.currentValue} → {metric.targetValue}
      </div>
    </li>
  );
}

function exampleRange(rows, page, total) {
  const offset = Number(page?.offset || 0);
  const from = rows.length ? offset + 1 : 0;
  return `Showing ${from}\u2013${offset + rows.length} of ${Number(page?.total ?? total)}`;
}

export function BriefSafeNowSection({
  trust,
  confirmedPage,
  needsPage,
  onConfirmedOffsetChange,
  onNeedsOffsetChange,
}) {
  const { confirmed, needsConfirmation, dataDefinition } = trust;
  const confirmedTotal = trustCount(trust, 'confirmed');
  const needsTotal = trustCount(trust, 'needsConfirmation');
  const dataDefinitionTotal = trustCount(trust, 'dataDefinition');
  return (
    <section
      className="rapid-panel metrics-brief-safe-section"
      data-testid="metrics-brief-safe-section"
    >
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Trust status</div>
          <h3>Brief-safe now vs needs confirmation</h3>
        </div>
      </div>
      <div className="metrics-brief-safe-split">
        <div className="metrics-brief-col is-safe" data-testid="metrics-brief-safe-confirmed">
          <div className="metrics-brief-col-head">
            <h4>Brief-safe now</h4>
            <span className="chip solid sm">{confirmedTotal} confirmed</span>
          </div>
          <p className="muted small">
            What leadership can say today: this RAPID shows measurable progress signals and a
            defensible current value model.
          </p>
          <p className="muted small" data-testid="metrics-confirmed-example-range">
            {exampleRange(confirmed, confirmedPage, confirmedTotal)} confirmed metric examples
          </p>
          <ul className="metrics-mini-card-list" data-testid="metrics-confirmed-metric-list">
            {confirmed.map((metric) => (
              <MetricMiniCard
                key={metric.id}
                metric={metric}
                testId={`metrics-confirmed-${metric.id}`}
              />
            ))}
          </ul>
          {confirmedPage ? (
            <PageViewerControls
              page={confirmedPage}
              pageSize={25}
              pageSizeOptions={[25]}
              label="confirmed metrics"
              onOffsetChange={onConfirmedOffsetChange}
            />
          ) : null}
        </div>
        <div
          className="metrics-brief-col is-needs"
          data-testid="metrics-brief-safe-needs-confirmation"
        >
          <div className="metrics-brief-col-head">
            <h4>Needs confirmation</h4>
            <span className="chip outline sm">{needsTotal} not brief-safe yet</span>
          </div>
          <p className="muted small">
            What this blocks: production claims require confirmed denominators, source owners, and
            metric definitions before these can be briefed as authoritative.
          </p>
          <div className="metrics-data-def-callout" data-testid="metrics-data-definition-list">
            <span className="chip warn sm">{dataDefinitionTotal} data-definition</span>
            <span className="muted small">
              {dataDefinition.map((m) => m.id).join(' · ') || 'No examples on this page'} — page
              examples from the {dataDefinitionTotal} data-definition subset of the {needsTotal}{' '}
              above, not an added bucket.
            </span>
          </div>
          <p className="muted small" data-testid="metrics-needs-example-range">
            {exampleRange(needsConfirmation, needsPage, needsTotal)} needs-confirmation metric
            examples
          </p>
          <ul className="metrics-mini-card-list" data-testid="metrics-needs-confirmation-list">
            {needsConfirmation.map((metric) => (
              <MetricMiniCard
                key={metric.id}
                metric={metric}
                testId={`metrics-needs-${metric.id}`}
              />
            ))}
          </ul>
          {needsPage ? (
            <PageViewerControls
              page={needsPage}
              pageSize={25}
              pageSizeOptions={[25]}
              label="needs-confirmation metrics"
              onOffsetChange={onNeedsOffsetChange}
            />
          ) : null}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 7.4 Cost Proof
// ---------------------------------------------------------------------------

function CostProofCard({ testId, eyebrow, title, calc, footnote, accent }) {
  return (
    <article
      className={`metrics-cost-card${accent ? ` accent-${accent}` : ''}`}
      data-testid={testId}
    >
      <div className="eyebrow">{eyebrow}</div>
      <h4>{title}</h4>
      <div className="metrics-cost-calc">{calc}</div>
      {footnote ? <div className="muted small">{footnote}</div> : null}
    </article>
  );
}

export function CostProofSection({ costAvoidance }) {
  const ca = costAvoidance || {};
  const derivations = ca.derivations || [];
  const [slot, rework, followup] = derivations;
  const cap = ca.capacity || {};
  const money = (value) => (value != null ? `$${Number(value).toLocaleString()}` : '—');
  return (
    <section
      className="rapid-panel metrics-cost-proof-section"
      data-testid="metrics-cost-proof-section"
    >
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Cost proof</div>
          <h3>How the {ca.displayValue || '$294K'} value is modeled</h3>
        </div>
        <span className="chip warn sm" data-testid="cost-proof-demo-confidence">
          {ca.confidence || 'Assumption-based estimate'}
        </span>
      </div>
      <div className="metrics-cost-card-grid">
        <CostProofCard
          testId="cost-proof-card-slot-time"
          eyebrow="Independently derived"
          title="Virtual PHA slot time"
          calc={slot?.calc}
          footnote={`${slot?.hours?.toLocaleString?.() ?? slot?.hours} labor hours avoided`}
          accent="blue"
        />
        <CostProofCard
          testId="cost-proof-card-rework"
          eyebrow="Independently derived"
          title="Rework avoided"
          calc={rework?.calc}
          footnote={`${rework?.hours?.toLocaleString?.() ?? rework?.hours} labor hours avoided`}
          accent="blue"
        />
        <CostProofCard
          testId="cost-proof-card-followup"
          eyebrow="Independently derived"
          title="Ancillary follow-up"
          calc={followup?.calc}
          footnote={`${followup?.hours?.toLocaleString?.() ?? followup?.hours} labor hours avoided`}
        />
        <CostProofCard
          testId="cost-proof-card-capacity"
          eyebrow="Capacity value (separate basis)"
          title="Recovered capacity"
          calc={`${cap.equivalentSlots?.toLocaleString?.() ?? cap.equivalentSlots} equivalent slots x ${money(
            cap.ratePerSlot,
          )} = ${money(cap.capacityCostAvoidance)}`}
          footnote="Not part of the labor-hour buckets."
        />
      </div>
      <div className="metrics-cost-rollup" data-testid="cost-proof-rollup">
        <div className="metrics-cost-rollup-formula">
          <span>
            Labor: {ca.totalLaborHours?.toLocaleString?.() ?? ca.totalLaborHours} hrs ×{' '}
            {money(ca.blendedRate)} = <strong>{money(ca.laborCostAvoidance)}</strong>
          </span>
          <span className="op">+</span>
          <span>
            Capacity: <strong>{money(cap.capacityCostAvoidance)}</strong>
          </span>
          <span className="op">=</span>
          <span>
            Total: <strong>{money(ca.totalCostAvoidance)}</strong> ({ca.displayValue || '$294K'})
          </span>
        </div>
        <p className="muted small">
          The two 1,200-hour buckets (slot-time 18,000×4 and rework 6,000×12) are independently
          derived — shown as separate cards, not a copy-paste.
        </p>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 7.5 Bowler Trend — flagship inline-SVG chart (Variant B)
// ---------------------------------------------------------------------------

function smoothPath(points) {
  if (!points.length) return '';
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[i - 1] || points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] || p2;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(1)} ${cp2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
  }
  return d;
}

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function BowlerTrendChart({ bowlerChart }) {
  const [hover, setHover] = useState(null);
  const monthly = bowlerChart?.monthly || [];
  if (!monthly.length) {
    return (
      <div className="rapid-empty-inline" data-testid="metrics-bowler-trend">
        No readings available for this metric.
      </div>
    );
  }

  // Geometry (viewBox units; the SVG scales fluidly to its container).
  const W = 760;
  const H = 340;
  const padL = 46;
  const padR = 96;
  const padT = 24;
  const padB = 44;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const n = monthly.length;
  const x = (i) => padL + (n === 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const y = (v) => padT + (1 - Math.max(0, Math.min(100, v)) / 100) * plotH;
  const baseY = y(0);

  const points = monthly.map((row, i) => {
    const actual = parsePct(row.actual);
    const target = parsePct(row.target);
    const gap = actual == null || target == null ? null : Number((actual - target).toFixed(1));
    const rag = gap == null ? 'Unknown' : gap >= 0 ? 'Green' : gap >= -10 ? 'Amber' : 'Red';
    return {
      i,
      label: (row.month || '').split(' ')[0],
      month: row.month,
      forecast: Boolean(row.forecast),
      actual,
      target,
      gap,
      rag,
      x: x(i),
      y: y(actual ?? 0),
    };
  });

  const forecastStart = points.findIndex((p) => p.forecast);
  const splitIndex = forecastStart === -1 ? points.length : forecastStart;
  const solidPts = points.slice(0, splitIndex);
  // Connect the dashed forecast back to the last actual point so the line is continuous.
  const dashedPts = points.slice(Math.max(0, splitIndex - 1));
  const currentPt = solidPts[solidPts.length - 1] || points[points.length - 1];
  const firstPt = points[0];
  const finalPt = points[points.length - 1];

  // Official DHA bowler "Linear (…)" trendline — least-squares fit across the full series.
  const fit = linearFit(points.map((p) => p.actual));
  const trendPath = fit
    ? `M ${x(0).toFixed(1)} ${y(fit.intercept).toFixed(1)} L ${x(n - 1).toFixed(1)} ${y(
        fit.intercept + fit.slope * (n - 1),
      ).toFixed(1)}`
    : '';

  const metricName = bowlerChart?.metric || 'Virtual PHA Utilization';
  const targetVal = parsePct(bowlerChart?.fy26Target) ?? 80;
  const targetY = y(targetVal);

  // Demonstration RAG band anchored to the FY26 target (At Risk 60 / On Track at target).
  const atRisk = 60;
  const bands = [
    {
      key: 'green',
      label: `On Track ≥ ${targetVal}%`,
      top: y(100),
      bottom: targetY,
      fill: 'rgba(22, 163, 74, 0.10)',
    },
    {
      key: 'amber',
      label: `At Risk ${atRisk}–${targetVal - 1}%`,
      top: targetY,
      bottom: y(atRisk),
      fill: 'rgba(217, 119, 6, 0.10)',
    },
    {
      key: 'red',
      label: `Off Track < ${atRisk}%`,
      top: y(atRisk),
      bottom: baseY,
      fill: 'rgba(220, 38, 38, 0.10)',
    },
  ];

  const areaPath = solidPts.length
    ? `${smoothPath(solidPts)} L ${currentPt.x.toFixed(1)} ${baseY} L ${solidPts[0].x.toFixed(1)} ${baseY} Z`
    : '';

  const ragColor = { Green: '#16a34a', Amber: '#d97706', Red: '#dc2626', Unknown: '#64748b' };
  const currentColor =
    ragColor[String(bowlerChart?.status)] || ragColor[currentPt?.rag] || '#dc2626';

  const animated = !prefersReducedMotion();
  const lastForecast = points[points.length - 1];
  const ariaSummary = `Virtual PHA Utilization trend. Baseline ${bowlerChart?.baseline}, current ${bowlerChart?.current}, FY26 target ${bowlerChart?.fy26Target}. Actuals through ${currentPt?.month} remain below target; forecast reaches ${lastForecast?.actual}% by ${lastForecast?.month}.`;

  const yTicks = [0, 20, 40, 60, 80, 100];

  return (
    <div className="metrics-bowler-chart-wrap">
      <svg
        className={`metrics-bowler-chart${animated ? ' is-animated' : ''}`}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={ariaSummary}
        data-testid="metrics-bowler-trend"
      >
        <defs>
          <linearGradient id="bowlerArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#17355c" stopOpacity="0.24" />
            <stop offset="100%" stopColor="#17355c" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* RAG threshold bands */}
        {bands.map((band) => (
          <g key={band.key}>
            <rect
              x={padL}
              y={band.top}
              width={plotW}
              height={Math.max(0, band.bottom - band.top)}
              fill={band.fill}
            />
            <text
              x={padL + plotW + 8}
              y={(band.top + band.bottom) / 2}
              className="metrics-bowler-band-label"
              dominantBaseline="middle"
            >
              {band.label}
            </text>
          </g>
        ))}

        {/* Y gridlines + axis labels */}
        {yTicks.map((tick) => (
          <g key={tick}>
            <line
              x1={padL}
              y1={y(tick)}
              x2={padL + plotW}
              y2={y(tick)}
              className="metrics-bowler-grid-line"
            />
            <text
              x={padL - 8}
              y={y(tick)}
              className="metrics-bowler-axis-label"
              textAnchor="end"
              dominantBaseline="middle"
            >
              {tick}
            </text>
          </g>
        ))}
        <text
          className="metrics-bowler-axis-title"
          x={14}
          y={padT + plotH / 2}
          transform={`rotate(-90 14 ${padT + plotH / 2})`}
          textAnchor="middle"
        >
          Utilization (%)
        </text>

        {/* X labels */}
        {points.map((p) => (
          <text
            key={p.i}
            x={p.x}
            y={H - padB + 18}
            className="metrics-bowler-axis-label"
            textAnchor="middle"
          >
            {p.label}
          </text>
        ))}

        {/* Target reference line */}
        <line
          x1={padL}
          y1={targetY}
          x2={padL + plotW}
          y2={targetY}
          className="metrics-bowler-target-line"
          data-testid="metrics-bowler-target-line"
        />
        <text x={padL + 4} y={targetY - 6} className="metrics-bowler-target-label">
          FY26 Target {bowlerChart?.fy26Target}
        </text>

        {/* Area fill under actuals */}
        {areaPath ? (
          <path d={areaPath} fill="url(#bowlerArea)" className="metrics-bowler-area" />
        ) : null}

        {/* Actual (solid) + forecast (dashed) lines */}
        <path d={smoothPath(solidPts)} className="metrics-bowler-line is-actual" fill="none" />
        <path d={smoothPath(dashedPts)} className="metrics-bowler-line is-forecast" fill="none" />

        {/* Linear regression trendline (dotted) — official DHA "Linear (…)" series */}
        {trendPath ? (
          <path
            d={trendPath}
            className="metrics-bowler-trendline"
            data-testid="metrics-bowler-trendline"
            fill="none"
          />
        ) : null}

        {/* Endpoint value labels (first actual … final reading) */}
        {firstPt ? (
          <text
            className="metrics-bowler-endpoint"
            x={firstPt.x}
            y={firstPt.y - 10}
            textAnchor="start"
          >
            {firstPt.actual}%
          </text>
        ) : null}
        {finalPt ? (
          <text
            className="metrics-bowler-endpoint"
            x={finalPt.x}
            y={finalPt.y - 10}
            textAnchor="end"
          >
            {finalPt.actual}%
          </text>
        ) : null}

        {/* Per-month focusable points + tooltip hit targets */}
        {points.map((p) => (
          <circle
            key={p.i}
            cx={p.x}
            cy={p.y}
            r={p.i === currentPt.i ? 6 : 4}
            className={`metrics-bowler-point${p.forecast ? ' is-forecast' : ''}${p.i === currentPt.i ? ' is-current' : ''}`}
            style={p.i === currentPt.i ? { fill: currentColor } : undefined}
            tabIndex={0}
            role="button"
            aria-label={`${p.month}: ${p.actual}% ${p.forecast ? 'forecast' : 'actual'}, target ${p.target}%, gap ${p.gap}`}
            data-testid={`metrics-bowler-point-${slug(p.month)}`}
            onMouseEnter={() => setHover(p.i)}
            onMouseLeave={() => setHover((cur) => (cur === p.i ? null : cur))}
            onFocus={() => setHover(p.i)}
            onBlur={() => setHover((cur) => (cur === p.i ? null : cur))}
          >
            <title>{`${p.month}: ${p.actual}% (${p.forecast ? 'Forecast' : 'Actual'}) · target ${p.target}% · gap ${p.gap}`}</title>
          </circle>
        ))}

        {/* Current value pill */}
        {currentPt ? (
          <g
            className="metrics-bowler-current-pill"
            transform={`translate(${currentPt.x}, ${currentPt.y - 16})`}
          >
            <rect x={-24} y={-18} width={48} height={20} rx={10} style={{ fill: currentColor }} />
            <text
              x={0}
              y={-8}
              textAnchor="middle"
              dominantBaseline="middle"
              className="metrics-bowler-current-text"
            >
              {bowlerChart?.current || `${currentPt.actual}%`}
            </text>
          </g>
        ) : null}

        {/* Hover/focus tooltip */}
        {hover != null && points[hover]
          ? (() => {
              const p = points[hover];
              const tx = Math.min(Math.max(p.x, padL + 70), padL + plotW - 70);
              const ty = Math.max(p.y - 64, padT + 6);
              return (
                <g
                  className="metrics-bowler-tooltip"
                  data-testid="metrics-bowler-tooltip"
                  transform={`translate(${tx}, ${ty})`}
                >
                  <rect x={-70} y={-4} width={140} height={56} rx={8} />
                  <text x={0} y={12} textAnchor="middle" className="tt-title">
                    {p.month} · {p.forecast ? 'Forecast' : 'Actual'}
                  </text>
                  <text x={0} y={30} textAnchor="middle" className="tt-row">
                    {p.actual}% vs target {p.target}%
                  </text>
                  <text x={0} y={46} textAnchor="middle" className="tt-row">
                    Gap {p.gap}
                  </text>
                </g>
              );
            })()
          : null}
      </svg>

      <div className="metrics-bowler-legend" aria-hidden="true">
        <span className="lg actual">{metricName} Performance</span>
        <span className="lg forecast">Forecast (through {finalPt?.label})</span>
        <span className="lg target">Target {bowlerChart?.fy26Target}</span>
        <span className="lg trend">Linear ({metricName})</span>
      </div>
    </div>
  );
}

export function BowlerTrendSection({ bowlerChart, children }) {
  return (
    <section className="rapid-panel metrics-bowler-section" data-testid="metrics-bowler-section">
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Performance proof</div>
          <h3>{bowlerChart?.metric || 'Virtual PHA Utilization'} — are we on track?</h3>
        </div>
        <div className="metrics-bowler-statrow">
          <span className="metrics-bowler-stat">
            <span className="k">Baseline</span>
            <span className="v">{bowlerChart?.baseline}</span>
          </span>
          <span className="metrics-bowler-stat is-current">
            <span className="k">Current</span>
            <span className="v">{bowlerChart?.current}</span>
          </span>
          <span className="metrics-bowler-stat">
            <span className="k">FY26 Target</span>
            <span className="v">{bowlerChart?.fy26Target}</span>
          </span>
          <span className="metrics-bowler-stat">
            <span className="k">Confidence</span>
            <span className="v is-text">Assumption-based estimate</span>
          </span>
        </div>
      </div>

      <div className="metrics-bowler-meta" data-testid="metrics-bowler-meta">
        <span className="metrics-bowler-metacell">
          <span className="k">Tier</span>
          <span className="v">{bowlerChart?.tier ?? '—'}</span>
        </span>
        <span className="metrics-bowler-metacell">
          <span className="k">Leading / Lagging</span>
          <span className="v">
            {bowlerChart?.leadingLagging ||
              (String(bowlerChart?.type || '').includes('Leading') ? 'Leading' : 'Lagging')}
          </span>
        </span>
        <span className="metrics-bowler-metacell">
          <span className="k">FY26 goal</span>
          <span className="v">{bowlerChart?.fy26Target}</span>
        </span>
        <span className="metrics-bowler-metacell">
          <span className="k">Threshold</span>
          <span className="v">
            {bowlerChart?.operator ? `${bowlerChart.operator} ` : ''}
            {bowlerChart?.fy26Target}
          </span>
        </span>
        <span className="metrics-bowler-metacell">
          <span className="k">As of</span>
          <span className="v">{bowlerChart?.currentDate || '—'}</span>
        </span>
      </div>

      <BowlerTrendChart bowlerChart={bowlerChart} />

      <p className="metrics-bowler-summary" data-testid="metrics-bowler-summary">
        Actuals remain below the FY26 target; the forecast reaches target only if denominator and
        capacity assumptions hold.
      </p>

      <div className="metrics-bowler-notes">
        {bowlerChart?.factors?.length ? (
          <div className="metrics-bowler-factors" data-testid="metrics-bowler-factors">
            <span className="chip outline sm">Factors impacting performance</span>
            <ul className="rapid-inline-list">
              {bowlerChart.factors.map((factor) => (
                <li key={factor}>{factor}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {bowlerChart?.wayAhead ? (
          <div className="metrics-bowler-way-ahead" data-testid="metrics-bowler-way-ahead">
            <span className="chip outline sm">Way ahead</span>
            <p>{bowlerChart.wayAhead}</p>
          </div>
        ) : null}
      </div>

      {/* Deterministic RAG grid — kept beneath as the text equivalent + E2E proof. */}
      {children}
    </section>
  );
}

// ---------------------------------------------------------------------------
// 7.7 Trust Gaps
// ---------------------------------------------------------------------------

export function TrustGapsSection({ gaps }) {
  return (
    <section
      className="rapid-panel metrics-trust-gaps-section"
      data-testid="metrics-trust-gaps-section"
    >
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Trust gaps</div>
          <h3>What must be resolved before a production value claim</h3>
        </div>
      </div>
      <div className="metrics-trust-gap-grid">
        {gaps.map((gap) => (
          <article className="metrics-trust-gap-card" data-testid={gap.testId} key={gap.testId}>
            <div className="row spread">
              <h4>{gap.title}</h4>
              {gap.linkedId ? <span className="chip outline sm">{gap.linkedId}</span> : null}
            </div>
            <p className="metrics-gap-why">
              <span className="k">Why it matters:</span> {gap.why}
            </p>
            <p className="metrics-gap-blocks">
              <span className="k">What it blocks:</span> {gap.blocks}
            </p>
            <div className="metrics-gap-meta muted small">
              <span>Owner: {gap.owner}</span>
              <span className="chip warn sm">{gap.statusLabel}</span>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 7.8 Sustainment
// ---------------------------------------------------------------------------

export function SustainmentSection({ cadence, sustainmentOwner, handoffOwner, onOpenCloseout }) {
  return (
    <section
      className="rapid-panel metrics-sustainment-section"
      data-testid="metrics-sustainment-section"
    >
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Sustainment</div>
          <h3>How value is checked after the RAPID closes</h3>
        </div>
        <button
          type="button"
          className="btn ghost sm"
          data-testid="metrics-sustainment-open-closeout"
          onClick={onOpenCloseout}
        >
          Open Closeout / Handoff
        </button>
      </div>
      <div className="metrics-sustainment-grid">
        <div className="metrics-sustainment-block">
          <h4>Review cadence</h4>
          <p className="metrics-sustainment-cadence" data-testid="metrics-sustainment-cadence">
            {cadence || '30 / 60 / 90 days, then quarterly'}
          </p>
          <p className="muted small">Planning cadence — does not assert production validation.</p>
        </div>
        <div className="metrics-sustainment-block" data-testid="metrics-sustainment-owner">
          <h4>Owner</h4>
          <p>{sustainmentOwner || 'Owner needed'}</p>
          <p className="muted small">Handoff owner: {handoffOwner || 'Owner needed'}</p>
        </div>
        <div className="metrics-sustainment-block">
          <h4>What must be reviewed</h4>
          <ul className="rapid-inline-list">
            <li>Virtual PHA utilization vs FY26 target.</li>
            <li>Incomplete IMR requirements at screening.</li>
            <li>Confirmed data-owner coverage and denominator definition.</li>
          </ul>
        </div>
      </div>
    </section>
  );
}
